import * as React from "react";
import { connect, Dispatch } from "react-redux";
import ProgressBar from "@luminopia/react-toolbox/lib/progress_bar";
import { List, ListItem } from "@luminopia/react-toolbox/lib/list";
import { Table, TableCell, TableHead, TableRow } from "@luminopia/react-toolbox/lib/table";
import { Button } from "@luminopia/react-toolbox/lib/button";
import Search from "core/components/search";
import SearchBar from "./SearchBar";
import Card from "../components/Card";
import { formatPercent } from "core/utils/number-helpers";
import { IState, selectReviewers, historyPushRequest, getTechnicianPhysiciansRequest } from "../store";
import { IReviewer } from "../store/reviewers/model";
import { AdherenceAPI, AdherenceResponseItem, AdherenceSummary } from "core/api/adherence-client";
import * as classNames from "./SubjectsListPage.css";
import { subjectShowRoute } from "../routes";
import { actions as mainActions } from "../store/main";
import safeSessionStorage from 'core/store/safe-session-storage'

interface Physician {
  firstName: string;
  lastName: string;
  npi: string;
}

interface IStateProps {
  role: string;
  token: string;
  currentReviewer: IReviewer | null;
  physicians: Physician[];
}

interface IOwnProps {
  treatmentStatus?: "ACTIVE" | "NOT ACTIVE";
  currentReviewer: IReviewer | null;
  onSearch?: (isAdmin: boolean, npis: string[]) => void;
}

interface IDispatchProps {
  onClickSubject: (subject: AdherenceResponseItem) => void;
  onMount?: (isAdmin: boolean, hasSubjects: boolean) => void;
  showSnackbar: (msg: string) => void;
}

export interface IProps extends IStateProps, IOwnProps, IDispatchProps {}

interface IComponentState {
  isFetching: boolean;
  adherenceData: AdherenceResponseItem[];
  adherenceSummary: AdherenceSummary | null;
  filterValue: string;
  isSearchSubmitted: boolean;
  filteredNPIs: string[];

  physician: Physician | null;

  sortColumn: string;
  sortDirection: "asc" | "desc";
}

const hasAdmin = (role: string) => role === "studyAdmin";

function getInitialPhysician(
  currentReviewer: IReviewer | null,
  physicians: Physician[] | null
): Physician | null {
  if (!currentReviewer) return null;
  if (!currentReviewer.isTechnician) {
    return currentReviewer;
  }
  if (physicians && physicians.length === 1) {
    return physicians[0];
  }
  return null;
}

function buildMailtoRequestInfo(physician: Physician, currentReviewer: IReviewer) {
  const subject = "Request for patient list and access codes";
  const body = `Dear CoAssist Pharmacy,

Please fax the latest list of our patients and access codes for Physician name ${physician.firstName} ${physician.lastName} and NPI ${physician.npi}. Please confirm the fax number you have for our office is [INSERT FAX NUMBER].

Regards,

${currentReviewer.firstName} ${currentReviewer.lastName}`;

  return `mailto:coassist.pharmacy@assistrx.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function getCacheKey(treatmentStatus: string): string {
  return `adherenceData_${treatmentStatus}`;
}

export class SubjectsListPage extends React.Component<IProps, IComponentState> {
  private adherenceApi: AdherenceAPI;

  constructor(props: IProps) {
    super(props);

    this.adherenceApi = new AdherenceAPI(props.token);

    this.state = {
      isFetching: true,
      adherenceData: [],
      adherenceSummary: null,
      filterValue: "",
      isSearchSubmitted: false,
      filteredNPIs: [],
      physician: getInitialPhysician(props.currentReviewer, props.physicians),
      sortColumn: "accessCode",
      sortDirection: "asc",
    };
  }

  componentWillReceiveProps(nextProps: IProps) {
    if (
      nextProps.physicians !== this.props.physicians ||
      nextProps.currentReviewer !== this.props.currentReviewer
    ) {
      this.setState({
        physician: getInitialPhysician(nextProps.currentReviewer, nextProps.physicians),
      });
    }
  }

  async componentDidMount() {
    const isAdmin = hasAdmin(this.props.role);
    const cacheKey = getCacheKey(this.props.treatmentStatus as string);
    const stored = safeSessionStorage.get(cacheKey);

    if (stored) {
      const parsed = JSON.parse(stored);
      this.setState({
        adherenceData: parsed.adherenceData || [],
        adherenceSummary: parsed.adherenceSummary || null,
        isFetching: false,
      });
      return;
    }

    if (!isAdmin) {
      await this.fetchAdherenceData();
      if (this.props.onMount) {
        const adherenceData = this.state.adherenceData;
        const hasSubjects = adherenceData && adherenceData.length > 0;
        this.props.onMount(isAdmin, hasSubjects);
      }
    } else {
      safeSessionStorage.remove(cacheKey);
      this.setState({ isFetching: false });
    }
  }

  async fetchAdherenceData(npis: string[] = []) {
    this.setState({ isFetching: true, adherenceData: [], adherenceSummary: null });
    const cacheKey = getCacheKey(this.props.treatmentStatus as string);

    try {
      const isAdmin = hasAdmin(this.props.role);
      const isActive = this.props.treatmentStatus === "ACTIVE";
      const response = await this.adherenceApi.getAdherence(
        isActive,
        isAdmin ? npis : undefined
      );

      if (!response.adherenceData || response.adherenceData.length === 0) {
        this.setState({ adherenceData: [], adherenceSummary: null, isFetching: false });
        if (isAdmin) {
          this.props.showSnackbar("No patients found");
        }
        return;
      }

      this.setState({
        adherenceData: response.adherenceData,
        adherenceSummary: response.summary || null,
        isFetching: false,
      });

      safeSessionStorage.set(
        cacheKey,
        JSON.stringify({
          adherenceData: response.adherenceData,
          adherenceSummary: response.summary || null,
        })
      );
    } catch (error) {
      console.error("Error fetching adherence data:", error);
      this.setState({ isFetching: false, adherenceData: [], adherenceSummary: null });
      if (hasAdmin(this.props.role)) {
        this.props.showSnackbar("No patients found");
      }
    }
  }

  handleSearchSubmit = (npis: string[]) => {
    this.setState({ filteredNPIs: npis, isSearchSubmitted: true });
    if (this.props.onSearch) {
      const isAdmin = hasAdmin(this.props.role);
      this.props.onSearch(isAdmin, npis);
    }
    this.fetchAdherenceData(npis);
  };

  handleChangeFilterValue = (value: string) => {
    this.setState({ filterValue: value });
  };

  handleSort = (column: string) => {
    this.setState((prevState) => ({
      sortColumn: column,
      sortDirection:
        prevState.sortColumn === column
          ? prevState.sortDirection === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  renderSortIcon(column: string) {
    const { sortColumn, sortDirection } = this.state;
    if (column === sortColumn) {
      return (
        <i className={`material-icons ${classNames.sortIcon}`}>
          {sortDirection === "asc" ? "arrow_upward" : "arrow_downward"}
        </i>
      );
    }
    return null;
  }

  render() {
    const { role, treatmentStatus } = this.props;
    const { isFetching, adherenceData, isSearchSubmitted } = this.state;
    const isAdmin = hasAdmin(role);

    const shouldRenderEverything = isAdmin || isSearchSubmitted || adherenceData.length > 0;

    return (
      <div>
        {isAdmin && <SearchBar onSubmit={this.handleSearchSubmit} />}
        <h1 className={classNames.pageTitle}>
          {treatmentStatus === "ACTIVE" ? "Active" : "Inactive"} Patients
        </h1>

        {isFetching ? (
          <ProgressBar type="circular" mode="indeterminate" multicolor />
        ) : shouldRenderEverything ? (
          this.renderEverything(isAdmin)
        ) : null}
      </div>
    );
  }

  renderEverything(isAdmin: boolean) {
    const { currentReviewer, physicians, onClickSubject, treatmentStatus } = this.props;
    const {
      adherenceData,
      adherenceSummary,
      filterValue,
      physician,
      sortColumn,
      sortDirection,
    } = this.state;

    if (!currentReviewer) return null;

    const isTechnicianWithNoPhysicians =
      currentReviewer.isTechnician &&
      currentReviewer.technicianNPIs &&
      currentReviewer.technicianNPIs.length === 0;

    const compareValue = filterValue.toUpperCase();
    const filteredData = adherenceData.filter((subject) => {
      const compareValue = filterValue.toUpperCase();
      return (
        (subject.accessCode && subject.accessCode.toUpperCase().includes(compareValue)) ||
        (subject.prescribingNpi && subject.prescribingNpi.toUpperCase().includes(compareValue)) ||
        (subject.physicianName && subject.physicianName.toUpperCase().includes(compareValue))
      );
    });

    const sortedData = filteredData.slice().sort((a, b) => {
      const sortMultiplier = sortDirection === "asc" ? 1 : -1;

      switch (sortColumn) {
        case "pastMonthAdherence": {
          const aPMA = a.pastMonthAdherence == null ? 0 : a.pastMonthAdherence;
          const bPMA = b.pastMonthAdherence == null ? 0 : b.pastMonthAdherence;
          return sortMultiplier * (aPMA - bPMA);
        }
        case "totalAdherence": {
          const aTA = a.totalAdherence == null ? 0 : a.totalAdherence;
          const bTA = b.totalAdherence == null ? 0 : b.totalAdherence;
          return sortMultiplier * (aTA - bTA);
        }
        case "firstDayOfTreatment": {
          const aVal = a.firstDayOfTreatment || "";
          const bVal = b.firstDayOfTreatment || "";
          return sortMultiplier * aVal.localeCompare(bVal);
        }
        case "prescribingNpi": {
          const aVal = a.prescribingNpi || "";
          const bVal = b.prescribingNpi || "";
          return sortMultiplier * aVal.localeCompare(bVal);
        }
        case "physicianName": {
          const aVal = a.physicianName || "";
          const bVal = b.physicianName || "";
          return sortMultiplier * aVal.localeCompare(bVal);
        }
        case "accessCode":
          default: {
          // Only push archived to bottom when sorting by accessCode ascending
          if (sortColumn === "accessCode" && sortDirection === "asc") {
            const aIsArchived = a.archivedAt !== null;
            const bIsArchived = b.archivedAt !== null;

            if (aIsArchived && !bIsArchived) return 1;
            if (!aIsArchived && bIsArchived) return -1;
          }

          const aVal = a.accessCode || "";
          const bVal = b.accessCode || "";
          return sortMultiplier * aVal.localeCompare(bVal);
        }
      }
    });

    const percentagePrecision = isAdmin ? 2 : 0;

    return (
      <div>
        {/* If admin => show summary table */}
        {isAdmin && adherenceSummary && (
          <Card>
            <Table selectable={false}>
              <TableHead>
                <TableCell>Global Adherence</TableCell>
                <TableCell>No. of Codes</TableCell>
                <TableCell>Median</TableCell>
                <TableCell>Mean</TableCell>
              </TableHead>
              <TableRow>
                <TableCell>Total Adherence</TableCell>
                <TableCell>{adherenceSummary.numberOfCodes}</TableCell>
                <TableCell>
                  {formatPercent(adherenceSummary.medianTotalAdherence || 0, 2)}
                </TableCell>
                <TableCell>
                  {formatPercent(adherenceSummary.meanTotalAdherence || 0, 2)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Past Week Adherence</TableCell>
                <TableCell>{adherenceSummary.numberOfCodes}</TableCell>
                <TableCell>
                  {formatPercent(adherenceSummary.medianPastWeekAdherence || 0, 2)}
                </TableCell>
                <TableCell>
                  {formatPercent(adherenceSummary.meanPastWeekAdherence || 0, 2)}
                </TableCell>
              </TableRow>
            </Table>
          </Card>
        )}

        {/* If technician has no assigned physicians => warning */}
        {isTechnicianWithNoPhysicians && (
          <div className={classNames.isTechnicianNoPhysicians}>
            <p>
              The physician who granted you access to patients is no longer sharing them with you.
              Please contact the physician if you still require access.
            </p>
          </div>
        )}

        {/* If we have data, show local “Search by Access Code” */}
        {adherenceData.length > 0 && (
          <Search
            value={this.state.filterValue}
            label={
              isAdmin
                ? "Search by Access Code or Physician"
                : currentReviewer && currentReviewer.isTechnician
                ? "Search by Access Code, Physician, or NPI"
                : "Search by Access Code"
            }
            onChange={this.handleChangeFilterValue}
            onClickClear={() => this.handleChangeFilterValue("")}
          />
        )}

        {/* The main card if we have sorted data and not a “no physicians” scenario */}
        {sortedData.length > 0 && !isTechnicianWithNoPhysicians && (
          <Card className={classNames.mainCard}>
            {/* If ACTIVE => highlight text */}
            {treatmentStatus === "ACTIVE" && (
              <List>
                <ListItem
                  leftIcon="warning"
                  ripple={false}
                  itemContent={
                    <span className={classNames.highlightExplanation}>
                      Patients highlighted in <mark>yellow</mark> have used Luminopia
                      less than 50% of standard dosage during the past month. This
                      calculation assumes 1 hour a day, 6 days a week as standard
                      dosage. Patients who have not yet had a month to use Luminopia
                      will not be highlighted.
                    </span>
                  }
                />
              </List>
            )}

            {/* Non-admin => “Request Info” */}
            {!isAdmin && (
              <List>
                <ListItem
                  leftIcon="info"
                  ripple={false}
                  legend="Check your weekly fax from the pharmacy for a table of access codes and patient names, or click below to request the information."
                />
                <ListItem ripple={false} className={classNames.requestInfo}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {/* If multiple physicians => show a dropdown */}
                    {physicians.length > 1 && (
                      <select
                        value={physician ? physician.npi : ""}
                        onChange={(ev) => {
                          const selectedPhysician =
                            physicians.find((p) => p.npi === ev.target.value) || null;
                          this.setState({ physician: selectedPhysician });
                        }}
                      >
                        <option value="">Physician</option>
                        {physicians.map((phys) => (
                          <option key={phys.npi} value={phys.npi}>
                            {phys.firstName} {phys.lastName}
                          </option>
                        ))}
                      </select>
                    )}

                    <Button
                      type="button"
                      label="Request Info"
                      primary
                      raised
                      onClick={() => {
                        if (physician && currentReviewer) {
                          window.location.href = buildMailtoRequestInfo(physician, currentReviewer);
                        }
                      }}
                      disabled={!physician || !currentReviewer}
                      className={classNames.requestInfoButton}
                      style={{ marginLeft: "1rem" }}
                    />
                  </div>
                </ListItem>
              </List>
            )}

            {/* Table: 4 columns for normal users, 6 for admin/technician */}
            <Table selectable={false} className={classNames.subjectTable}>
              <TableHead>
                <TableCell
                  className={classNames.sortableColumn}
                  onClick={() => this.handleSort("accessCode")}
                >
                  Access Code {this.renderSortIcon("accessCode")}
                </TableCell>
                <TableCell
                  className={classNames.sortableColumn}
                  onClick={() => this.handleSort("firstDayOfTreatment")}
                >
                  Treatment Start Date {this.renderSortIcon("firstDayOfTreatment")}
                </TableCell>
                <TableCell
                  className={classNames.sortableColumn}
                  onClick={() => this.handleSort("pastMonthAdherence")}
                >
                  Past Month Adherence % {this.renderSortIcon("pastMonthAdherence")}
                </TableCell>
                <TableCell
                  className={classNames.sortableColumn}
                  onClick={() => this.handleSort("totalAdherence")}
                >
                  Total Adherence % {this.renderSortIcon("totalAdherence")}
                </TableCell>

                {/* If admin or a technician => show NPI + Physician columns */}
                {(isAdmin || currentReviewer.isTechnician) && (
                  <>
                    <TableCell
                      className={classNames.sortableColumn}
                      onClick={() => this.handleSort("prescribingNpi")}
                    >
                      NPI {this.renderSortIcon("prescribingNpi")}
                    </TableCell>
                    <TableCell
                      className={classNames.sortableColumn}
                      onClick={() => this.handleSort("physicianName")}
                    >
                      Physician {this.renderSortIcon("physicianName")}
                    </TableCell>
                  </>
                )}
              </TableHead>

              {sortedData.map((subject) => {
                const showAdherenceWarning =
                  subject.isActive == true &&
                  subject.pastMonthAdherence !== null &&
                  subject.pastMonthAdherence < 0.5 &&
                  subject.firstDayOfTreatment;
                const isArchived = subject.archivedAt !== null;

                return (
                  <TableRow
                    key={subject.patientId}
                    onClick={() => onClickSubject(subject)}
                    className={
                      isArchived
                        ? classNames.archived
                        : showAdherenceWarning
                          ? classNames.isNotTreatmentAdherent
                          : ""
                    }
                  >
                    <TableCell>{subject.accessCode || "N/A"}</TableCell>

                    <TableCell>
                      {subject.firstDayOfTreatment
                        ? subject.firstDayOfTreatment.slice(0, 10)
                        : "Not Started Treatment"}
                    </TableCell>

                    <TableCell>
                      {subject.firstDayOfTreatment
                        ? formatPercent(subject.pastMonthAdherence || 0, percentagePrecision)
                        : "-"}
                    </TableCell>

                    <TableCell>
                      {subject.firstDayOfTreatment
                        ? formatPercent(subject.totalAdherence || 0, percentagePrecision)
                        : "-"}
                    </TableCell>

                    {/* Extra columns for admin/technician */}
                    {(isAdmin || currentReviewer.isTechnician) && (
                      <>
                        <TableCell>{subject.prescribingNpi || ""}</TableCell>
                        <TableCell>{subject.physicianName || ""}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </Table>
          </Card>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: IState, ownProps: IOwnProps): IStateProps => ({
  role: state.login.role,
  token: state.login.token || "",
  currentReviewer: state.reviewers.currentReviewer || null,
  physicians: selectReviewers(state),
});

const mapDispatchToProps = (dispatch: Dispatch<void>, ownProps: IOwnProps) => ({
  onClickSubject: (subject: AdherenceResponseItem) => {
    safeSessionStorage.set("selectedSubject", JSON.stringify(subject));
    dispatch(historyPushRequest(subjectShowRoute(subject.patientId)));
  },
  onMount: (isAdmin: boolean, hasSubjects: boolean) => {
    const { currentReviewer } = ownProps;
    if (currentReviewer && currentReviewer.isTechnician) {
      dispatch(getTechnicianPhysiciansRequest(currentReviewer.id));
    }
  },
  showSnackbar: (msg: string) => dispatch(mainActions.snackbarSetRequest(msg)),
});

export default connect<IStateProps, IDispatchProps, IOwnProps, IState>(
  mapStateToProps,
  mapDispatchToProps
)(SubjectsListPage);



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/pages/SubjectsListPage.tsx